import React, { useState } from 'react';
import { X, AlertTriangle, UserCheck, Ban, PauseCircle, Moon, UserX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { type UpdateUserStatusRequest, type User } from '../../../types';
import { useUpdateUserStatus } from '../../../hooks/useApi';

type AdminSettableStatus = UpdateUserStatusRequest['status'];

interface UpdateUserStatusModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateUserStatusModal: React.FC<UpdateUserStatusModalProps> = ({ user, isOpen, onClose, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState<AdminSettableStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateStatusMutation = useUpdateUserStatus();

  /** Maps player row to the status values the admin PATCH endpoint accepts. */
  const userToAdminStatus = (u: User): AdminSettableStatus => {
    if (u.banned || u.status === 'banned') return 'banned';
    if (u.status === 'suspended') return 'suspended';
    return 'active';
  };

  // Set initial status when modal opens
  React.useEffect(() => {
    if (isOpen && user) {
      setSelectedStatus(userToAdminStatus(user));
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const displayStatusKey: User['status'] = user.banned ? 'banned' : user.status;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentStatus = userToAdminStatus(user);

    if (!selectedStatus || selectedStatus === currentStatus) {
      return;
    }

    try {
      setIsSubmitting(true);
      await updateStatusMutation.mutateAsync({
        userId: user.id,
        data: { status: selectedStatus },
      });
      onSuccess();
      onClose();
    } catch {
      // useUpdateUserStatus onError shows the API message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedStatus(null);
      onClose();
    }
  };

  const getDisplayStatusConfig = (status: User['status']) => {
    const configs: Record<
      User['status'],
      {
        label: string;
        description: string;
        icon: typeof UserCheck;
        className: string;
        iconClassName: string;
      }
    > = {
      active: {
        label: 'Active',
        description: 'User can login and use the system normally',
        icon: UserCheck,
        className: 'bg-green-50 border-green-200 text-green-800',
        iconClassName: 'text-green-600',
      },
      inactive: {
        label: 'Inactive',
        description: 'Account marked inactive',
        icon: Moon,
        className: 'bg-slate-50 border-slate-200 text-slate-800',
        iconClassName: 'text-slate-600',
      },
      suspended: {
        label: 'Suspended',
        description: 'User cannot sign in until reactivated',
        icon: PauseCircle,
        className: 'bg-amber-50 border-amber-200 text-amber-900',
        iconClassName: 'text-amber-600',
      },
      banned: {
        label: 'Banned',
        description: 'User is banned and cannot login to the system',
        icon: Ban,
        className: 'bg-red-50 border-red-200 text-red-800',
        iconClassName: 'text-red-600',
      },
      terminated: {
        label: 'Closed',
        description: 'Account has been closed and cannot be used',
        icon: UserX,
        className: 'bg-neutral-100 border-neutral-300 text-neutral-800',
        iconClassName: 'text-neutral-600',
      },
    };

    return configs[status];
  };

  const getSelectableStatusConfig = (status: AdminSettableStatus) => {
    const configs: Record<
      AdminSettableStatus,
      {
        label: string;
        description: string;
        icon: typeof UserCheck;
        className: string;
        iconClassName: string;
      }
    > = {
      active: {
        label: 'Active',
        description: 'User can login and use the system normally',
        icon: UserCheck,
        className: 'bg-green-50 border-green-200 text-green-800',
        iconClassName: 'text-green-600',
      },
      suspended: {
        label: 'Suspended',
        description: 'User cannot sign in until reactivated',
        icon: PauseCircle,
        className: 'bg-amber-50 border-amber-200 text-amber-900',
        iconClassName: 'text-amber-600',
      },
      banned: {
        label: 'Banned',
        description: 'User is banned and cannot login to the system',
        icon: Ban,
        className: 'bg-red-50 border-red-200 text-red-800',
        iconClassName: 'text-red-600',
      },
    };

    return configs[status];
  };

  const statusOptions: AdminSettableStatus[] = ['active', 'banned'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center text-lg">
            <UserCheck className="h-5 w-5 mr-2 text-blue-600" />
            Update User Status
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Info */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium">{user.email}</p>
                <p className="text-sm text-muted-foreground">
                  Current status:{' '}
                  <span className="font-medium">{getDisplayStatusConfig(displayStatusKey).label}</span>
                </p>
              </div>
            </div>

            {/* Current Status Display */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Current Status</label>
              <div className={`flex items-center space-x-2 p-3 rounded-lg border ${getDisplayStatusConfig(displayStatusKey).className}`}>
                {React.createElement(getDisplayStatusConfig(displayStatusKey).icon, {
                  className: `h-5 w-5 ${getDisplayStatusConfig(displayStatusKey).iconClassName}`,
                })}
                <div>
                  <p className="font-medium">{getDisplayStatusConfig(displayStatusKey).label}</p>
                  <p className="text-xs opacity-75">{getDisplayStatusConfig(displayStatusKey).description}</p>
                </div>
              </div>
            </div>

            {/* Status Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Select New Status <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {statusOptions.map((status) => {
                  const config = getSelectableStatusConfig(status);
                  const isSelected = selectedStatus === status;
                  const currentStatus = userToAdminStatus(user);
                  const isCurrentStatus = status === currentStatus;

                  return (
                    <label
                      key={status}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : isCurrentStatus
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={isSelected}
                        onChange={() => setSelectedStatus(status)}
                        disabled={isCurrentStatus || isSubmitting}
                        className="mt-1"
                      />
                      <div className="flex items-center space-x-2 flex-1">
                        {React.createElement(config.icon, {
                          className: `h-5 w-5 ${config.iconClassName}`
                        })}
                        <div>
                          <p className="font-medium">{config.label}</p>
                          <p className="text-xs text-gray-600">{config.description}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Warning Message */}
            {selectedStatus && selectedStatus !== userToAdminStatus(user) && (
              <div className="flex items-start space-x-2 p-3 bg-yellow-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">
                    Confirm Status Change
                  </p>
                  <p className="text-yellow-700 mt-1">
                    Are you sure you want to change status from{' '}
                    <strong>{getSelectableStatusConfig(userToAdminStatus(user)).label}</strong> to{' '}
                    <strong>{getSelectableStatusConfig(selectedStatus).label}</strong>?
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !selectedStatus || selectedStatus === userToAdminStatus(user)}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  'Update Status'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdateUserStatusModal;
